export interface NormalizeStakeOptions {
  betMin: number
  betMax: number
  payoutMax: number
  odds: number
  drawCount?: number
  numBets?: number
  allowDecimals?: boolean
}

/** API/game-settings may return numeric fields as strings. */
export function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

function floorStakeAmount(stake: number, allowDecimals: boolean): number {
  if (!allowDecimals) {
    return Math.floor(stake)
  }
  return Math.floor(stake * 100) / 100
}

export function calcPotentialWin(
  stake: number,
  odds: number,
  drawCount = 1,
  numBets = 1
): number {
  const stakeValue = toNumber(stake)
  const oddsValue = toNumber(odds)
  if (!stakeValue || !oddsValue) return 0
  return stakeValue * oddsValue * drawCount * numBets
}

export function capStakeForPayoutMax(
  stake: number,
  odds: number,
  payoutMax: number,
  drawCount = 1,
  numBets = 1,
  allowDecimals = true
): number {
  const stakeValue = toNumber(stake)
  const oddsValue = toNumber(odds)
  const payoutCap = toNumber(payoutMax)
  if (payoutCap <= 0 || oddsValue <= 0) return stakeValue
  const multiplier = oddsValue * drawCount * numBets
  if (multiplier <= 0) return stakeValue
  return floorStakeAmount(payoutCap / multiplier, allowDecimals)
}

export function normalizeStakeValue(
  rawStake: number,
  options: NormalizeStakeOptions
): number {
  const allowDecimals = options.allowDecimals ?? true
  let stakeValue = toNumber(rawStake)
  const betMin = toNumber(options.betMin)
  const betMax = toNumber(options.betMax)
  const payoutMax = toNumber(options.payoutMax)
  const odds = toNumber(options.odds)
  const drawCount = options.drawCount ?? 1
  const numBets = options.numBets ?? 1

  if (betMin > 0 && stakeValue < betMin) {
    stakeValue = betMin
  }

  if (betMax > 0 && stakeValue > betMax) {
    stakeValue = betMax
  }

  if (payoutMax > 0 && odds > 0) {
    const maxStakeForPayout = capStakeForPayoutMax(
      stakeValue,
      odds,
      payoutMax,
      drawCount,
      numBets,
      allowDecimals
    )
    if (stakeValue > maxStakeForPayout) {
      stakeValue = maxStakeForPayout
    }
  }

  stakeValue = floorStakeAmount(stakeValue, allowDecimals)

  if (betMin > 0 && stakeValue < betMin) {
    const potentialAtMin = calcPotentialWin(betMin, odds, drawCount, numBets)
    if (payoutMax <= 0 || potentialAtMin <= payoutMax) {
      stakeValue = betMin
    }
  }

  return stakeValue
}

/** True potential win for display (stake × odds); only clips when still above cap. */
export function displayPotentialWin(
  stake: number,
  odds: number,
  payoutMax: number,
  drawCount = 1,
  numBets = 1
): number {
  const potentialWin = calcPotentialWin(stake, odds, drawCount, numBets)
  const payoutCap = toNumber(payoutMax)
  if (payoutCap <= 0) return potentialWin
  return potentialWin > payoutCap ? payoutCap : potentialWin
}
